import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { db } from '../../db/index.js';
import { createDeck, renameDeck, moveDeck, descendantIds } from '../../db/repo/decks.js';
import { useDecks } from '../../hooks/useDecks.js';
import Sheet from '../../components/Sheet.jsx';
import Button from '../../components/Button.jsx';
import DeckPicker from '../../components/DeckPicker.jsx';
import { TextInput } from '../../components/Field.jsx';
import { toast } from '../../components/Toaster.jsx';

/** mode: 'create' | 'edit' */
export default function DeckFormSheet(props) {
  return props.open ? <DeckForm {...props} /> : null;
}

function DeckForm({ open, onClose, mode, deck, parentId = null }) {
  const decks = useDecks();
  const navigate = useNavigate();
  const [name, setName] = useState(() => (mode === 'edit' ? (deck?.name ?? '') : ''));
  const [parent, setParent] = useState(() => (mode === 'edit' ? (deck?.parentId ?? null) : parentId));
  const [error, setError] = useState(null);

  const blocked = useMemo(
    () => (mode === 'edit' && deck && decks ? descendantIds(decks, deck.id) : []),
    [decks, deck, mode],
  );
  const hasOtherDecks = (decks ?? []).some((d) => !d.deletedAt && !blocked.includes(d.id));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the deck a name.');
      return;
    }
    try {
      if (mode === 'create') {
        const id = await createDeck(db, { name, parentId: parent || null });
        onClose();
        navigate(`/deck/${id}`);
        toast.success('Deck created');
      } else {
        if (name.trim() !== deck.name) await renameDeck(db, deck.id, name);
        if ((parent || null) !== (deck.parentId ?? null)) await moveDeck(db, deck.id, parent || null);
        onClose();
        toast.success('Deck saved');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const title = mode === 'edit' ? 'Rename or move' : parentId ? 'New subdeck' : 'New deck';

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={mode === 'create' && !parentId ? 'A deck holds cards about one topic.' : null}
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button size="lg" variant="primary" type="submit" form="deck-form">
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="deck-form" onSubmit={submit}>
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          placeholder="e.g. Renal"
          autoFocus
          error={error}
          enterKeyHint="done"
          autoCapitalize="sentences"
        />
        {hasOtherDecks ? (
          <DeckPicker
            label="Inside"
            title="Put it inside…"
            decks={decks}
            exclude={blocked}
            value={parent}
            onChange={setParent}
            noneLabel="Nothing (top level)"
            noneDescription="Shows up on its own on the home screen."
            icon="folder"
          />
        ) : null}
      </form>
    </Sheet>
  );
}
